import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Card } from '@/components/ui/card';

export interface ExecutionResponseData {
  success: boolean;
  responseTimeMs: number | null;
  httpStatusCode: number | null;
  responseBody: string | null;
  responseHeaders: Record<string, string> | null;
  errorMessage: string | null;
}

interface ExecutionDetailsProps {
  data: ExecutionResponseData;
  className?: string;
}

export function ExecutionDetails({ data, className = '' }: ExecutionDetailsProps) {
  const [showHeaders, setShowHeaders] = useState(false);
  const [showBody, setShowBody] = useState(true);

  // Format headers for display - sort alphabetically
  const formatHeaders = (headers: Record<string, string>) => {
    return Object.entries(headers)
      .sort(([a], [b]) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map(([key, value]) => ({ key, value }));
  };

  // Safely display text content (React escapes by default)
  const truncateBody = (body: string, maxLength = 2000) => {
    if (body.length <= maxLength) return body;
    return `${body.substring(0, maxLength)}...`;
  };

  return (
    <Card className={`p-6 space-y-4 ${className}`}>
      {/* Status and basic info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="space-y-1">
          <div className="text-sm font-medium text-gray-500">Status</div>
          <div
            className={`text-lg font-semibold ${data.success ? 'text-green-600' : 'text-red-600'}`}
          >
            {data.success ? 'Success' : 'Failed'}
          </div>
        </div>

        {data.httpStatusCode !== null && (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-500">HTTP Status</div>
            <div
              className={`text-lg font-semibold ${
                data.httpStatusCode >= 200 && data.httpStatusCode < 300
                  ? 'text-green-600'
                  : data.httpStatusCode >= 400
                    ? 'text-red-600'
                    : 'text-yellow-600'
              }`}
            >
              {data.httpStatusCode}
            </div>
          </div>
        )}

        {data.responseTimeMs !== null && (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-500">Response Time</div>
            <div
              className={`text-lg font-semibold ${
                data.responseTimeMs < 500
                  ? 'text-green-600'
                  : data.responseTimeMs < 2000
                    ? 'text-yellow-600'
                    : 'text-red-600'
              }`}
            >
              {data.responseTimeMs}ms
            </div>
          </div>
        )}

        {data.errorMessage && (
          <div className="space-y-1">
            <div className="text-sm font-medium text-gray-500">Error</div>
            <div className="text-sm text-red-600 break-words">{data.errorMessage}</div>
          </div>
        )}
      </div>

      {/* Response Headers */}
      {data.responseHeaders && (
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowHeaders(!showHeaders)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            {showHeaders ? (
              <ChevronDown className="h-4 w-4" />
            ) : (
              <ChevronRight className="h-4 w-4" />
            )}
            Response Headers ({Object.keys(data.responseHeaders).length})
          </button>

          {showHeaders && (
            <div className="mt-3 bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">
              <table className="w-full text-sm">
                <tbody>
                  {formatHeaders(data.responseHeaders).map(({ key, value }) => (
                    <tr key={key} className="border-b border-gray-200 last:border-0">
                      <td className="py-2 pr-4 font-medium text-gray-600 align-top whitespace-nowrap">
                        {key}:
                      </td>
                      <td className="py-2 text-gray-900 break-words">{value}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Response Body */}
      {data.responseBody && (
        <div className="border-t pt-4">
          <button
            type="button"
            onClick={() => setShowBody(!showBody)}
            className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
          >
            {showBody ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            Response Body (First 2000 characters)
          </button>

          {showBody && (
            <div className="mt-3 bg-gray-50 rounded-lg p-4">
              <pre className="text-xs text-gray-800 whitespace-pre-wrap break-words font-mono max-h-96 overflow-y-auto">
                {truncateBody(data.responseBody, 2000)}
              </pre>
            </div>
          )}
        </div>
      )}

      {/* No response data message */}
      {!data.responseHeaders && !data.responseBody && !data.errorMessage && (
        <div className="text-sm text-gray-500 italic">No response data available</div>
      )}
    </Card>
  );
}
